import React, { useState, useEffect } from 'react';
import { Shield, Save, Check, X, Wifi } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';

const ACLManagement = ({ embedded = false }) => {
  const [matrix, setMatrix] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPermissions();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles/');
      setRoles(res.data.results || res.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const updateRoleIPs = async (roleId, allowedIps) => {
    try {
      await api.patch(`/roles/${roleId}/`, { allowed_ips: allowedIps });
      toast.success('IP restrictions updated');
    } catch (error) {
      toast.error('Failed to update IP restrictions');
    }
  };

  const updateHistoryDaysLimit = async (roleId, days) => {
    try {
      await api.patch(`/roles/${roleId}/`, { history_days_limit: parseInt(days) || 0 });
      toast.success('History days limit updated');
    } catch (error) {
      toast.error('Failed to update history days limit');
    }
  };

  const toggleShowSalesBeforeReconciliation = async (roleId, currentValue) => {
    try {
      await api.patch(`/roles/${roleId}/`, { show_sales_before_reconciliation: !currentValue });
      toast.success('Reconciliation visibility updated');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await api.get('/permissions/matrix/');
      setMatrix(res.data.matrix);
      setModules(res.data.modules);
      setActions(res.data.actions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (roleIndex, module, action) => {
    setMatrix(prev => prev.map((role, idx) => {
      if (idx !== roleIndex) return role;
      return {
        ...role,
        permissions: {
          ...role.permissions,
          [module]: {
            ...role.permissions[module],
            [action]: !role.permissions[module][action]
          }
        }
      };
    }));
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      for (const role of matrix) {
        const permissions = [];
        for (const module of modules) {
          for (const action of actions) {
            permissions.push({
              module,
              action,
              is_allowed: role.permissions[module][action]
            });
          }
        }
        await api.post('/permissions/bulk_update/', {
          role_id: role.role_id,
          permissions
        });
      }
      toast.success('Permissions saved successfully! Users will see changes on next page load.');
      // Re-fetch to confirm save worked
      await fetchPermissions();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const moduleLabels = {
    dashboard: 'Dashboard',
    pos: 'POS',
    sales: 'Sales',
    staff: 'Staff Management',
    attendance: 'Attendance',
    inventory: 'Inventory',
    menu: 'Menu Management',
    expenses: 'Expenses',
    reconciliation: 'Reconciliation / Cash',
    wastage: 'Wastage',
    reports: 'Reports / Stock Suggestions',
    activity_log: 'Activity Log',
    ai_assistant: 'AI Assistant',
    users: 'User Management',
    settings: 'Settings',
    leaves: 'Leave Management',
    performance: 'Staff Performance',
  };

  const actionLabels = {
    view: 'View',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    approve: 'Approve',
    export: 'Export'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {embedded ? (
        <div className="flex justify-end">
          <button
            onClick={savePermissions}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
              
              <span className="hidden sm:inline">Role-Based Access Control (RBAC)</span>
              <span className="sm:hidden">RBAC</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-500">Control which features each role can access</p>
          </div>
          <button
            onClick={savePermissions}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        </div>
      )}

      {/* ACL Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 bg-gray-50 border-b">
          <p className="text-sm text-gray-600">
            <strong>Note:</strong> Super Admin and Admin have full access automatically. 
            Configure Manager and Cashier permissions below.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              {/* Role header row with colored backgrounds */}
              <tr>
                <th rowSpan={2} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 bg-gray-100 sticky left-0 z-10 border-b-2 border-gray-300 min-w-[180px]">
                  Module
                </th>
                {matrix.map((role, i) => {
                  const colors = [
                    'bg-blue-600 text-white',
                    'bg-emerald-600 text-white', 
                    'bg-orange-500 text-white',
                    'bg-purple-600 text-white'
                  ];
                  return (
                    <th
                      key={role.role_id}
                      colSpan={actions.length}
                      className={`px-4 py-3 text-center text-sm font-bold uppercase tracking-wider border-l-4 border-white ${colors[i] || colors[0]}`}
                    >
                      {role.role_name}
                    </th>
                  );
                })}
              </tr>
              {/* Action sub-headers */}
              <tr>
                {matrix.map((role, i) => {
                  const bgColors = ['bg-blue-50', 'bg-emerald-50', 'bg-orange-50', 'bg-purple-50'];
                  const borderColors = ['border-l-blue-200', 'border-l-emerald-200', 'border-l-orange-200', 'border-l-purple-200'];
                  return actions.map((action, j) => (
                    <th
                      key={`${role.role_id}-${action}`}
                      className={`px-1 py-2 text-center text-[10px] font-semibold text-gray-600 uppercase ${bgColors[i] || bgColors[0]} ${j === 0 ? `border-l-4 ${borderColors[i] || borderColors[0]}` : 'border-l border-gray-200'} border-b-2 border-gray-300`}
                    >
                      {actionLabels[action]}
                    </th>
                  ));
                })}
              </tr>
            </thead>
            <tbody>
              {modules.map((module, mIdx) => (
                <tr key={module} className={`${mIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-yellow-50 transition-colors`}>
                  <td className={`px-4 py-3 font-medium text-gray-800 sticky left-0 z-10 border-b border-gray-200 ${mIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-sm">{moduleLabels[module]}</span>
                  </td>
                  {matrix.map((role, roleIndex) => {
                    const borderColors = ['border-l-blue-300', 'border-l-emerald-300', 'border-l-orange-300', 'border-l-purple-300'];
                    return actions.map((action, j) => (
                      <td
                        key={`${role.role_id}-${module}-${action}`}
                        className={`px-1 py-2 text-center border-b border-gray-200 ${j === 0 ? `border-l-4 ${borderColors[roleIndex] || borderColors[0]}` : 'border-l border-gray-100'}`}
                      >
                        <button
                          onClick={() => togglePermission(roleIndex, module, action)}
                          className={`w-7 h-7 rounded-md flex items-center justify-center mx-auto transition ${
                            role.permissions[module]?.[action]
                              ? 'bg-green-100 text-green-600 hover:bg-green-200 ring-1 ring-green-300'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {role.permissions[module]?.[action] ? (
                            <Check size={14} />
                          ) : (
                            <X size={14} />
                          )}
                        </button>
                      </td>
                    ));
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ACL Cards - Mobile */}
      <div className="md:hidden space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-700">
            <strong>Note:</strong> Super Admin & Admin have full access. Configure Manager & Cashier below.
          </p>
        </div>
        {modules.map((module) => (
          <div key={module} className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800">{moduleLabels[module]}</h3>
            </div>
            <div className="p-3 space-y-3">
              {matrix.map((role, roleIndex) => (
                <div key={role.role_id}>
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2 capitalize">{role.role_name}</p>
                  <div className="flex flex-wrap gap-2">
                    {actions.map((action) => (
                      <button
                        key={`${role.role_id}-${module}-${action}`}
                        onClick={() => togglePermission(roleIndex, module, action)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${
                          role.permissions[module][action]
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-gray-400 border border-gray-200'
                        }`}
                      >
                        {role.permissions[module][action] ? <Check size={12} /> : <X size={12} />}
                        {actionLabels[action]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* History Days Limit */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
          
          Sales/Purchases History Visibility
        </h2>
        <p className="text-sm text-gray-500 mb-4">Control how many days of historical sales and purchases each role can see. Set to 0 for unlimited.</p>
        <div className="space-y-4">
          {roles.filter(r => r.name === 'manager' || r.name === 'cashier').map((role) => (
            <div key={role.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 w-24 capitalize flex-shrink-0">{role.name}</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="number"
                  defaultValue={role.history_days_limit || 0}
                  onBlur={(e) => {
                    const newVal = parseInt(e.target.value) || 0;
                    if (newVal !== (role.history_days_limit || 0)) {
                      updateHistoryDaysLimit(role.id, newVal);
                    }
                  }}
                  min="0"
                  placeholder="0"
                  className="w-24 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">days {(role.history_days_limit || 0) === 0 ? '(unlimited)' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reconciliation Sales Visibility */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
          
          Reconciliation Sales Visibility
        </h2>
        <p className="text-sm text-gray-500 mb-4">Allow roles to see sales details on the reconciliation page <strong>before</strong> the day is reconciled.</p>
        <div className="space-y-3">
          {roles.filter(r => r.name === 'manager' || r.name === 'cashier').map((role) => (
            <div key={role.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <span className="text-sm font-medium text-gray-700 capitalize">{role.name}</span>
                <p className="text-xs text-gray-400">
                  {role.show_sales_before_reconciliation ? 'Can see sales before reconciliation' : 'Sales hidden until reconciled'}
                </p>
              </div>
              <button
                onClick={() => toggleShowSalesBeforeReconciliation(role.id, role.show_sales_before_reconciliation)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  role.show_sales_before_reconciliation ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  role.show_sales_before_reconciliation ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* IP Restrictions */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
          
          IP Login Restrictions
        </h2>
        <p className="text-sm text-gray-500 mb-4">Restrict login to specific IP addresses per role. Leave blank to allow all IPs.</p>
        <div className="space-y-4">
          {roles.filter(r => r.name === 'manager' || r.name === 'cashier').map((role) => (
            <div key={role.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 w-24 capitalize flex-shrink-0">{role.name}</label>
              <input
                type="text"
                defaultValue={role.allowed_ips || ''}
                onBlur={(e) => {
                  if (e.target.value !== (role.allowed_ips || '')) {
                    updateRoleIPs(role.id, e.target.value);
                  }
                }}
                placeholder="e.g. 192.168.1.100, 192.168.1.101"
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <h3 className="font-medium text-gray-800 mb-3">Permission Legend</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-100 text-green-600 rounded flex items-center justify-center">
              <Check size={14} />
            </div>
            <span>Allowed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-100 text-gray-400 rounded flex items-center justify-center">
              <X size={14} />
            </div>
            <span>Denied</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <h4 className="font-medium text-gray-700 mb-2">Action Descriptions:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600">
            <div><strong>View:</strong> See/read data</div>
            <div><strong>Add:</strong> Create new records</div>
            <div><strong>Edit:</strong> Modify existing records</div>
            <div><strong>Delete:</strong> Remove records</div>
            <div><strong>Approve:</strong> Approve pending items</div>
            <div><strong>Export:</strong> Download/print reports</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ACLManagement;
